/**
 * SmartAssistantPanel - Right-side collapsible utility panel
 * Features: AI Chat, Support Tickets, Market Rates, Notifications, Activity Log
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  X, ChevronRight, ChevronLeft, MessageSquare, Ticket, TrendingUp,
  Bell, Activity, Zap, Send, Paperclip, Search, Plus, AlertTriangle,
  CheckCircle, Clock, ArrowUpRight, ArrowDownRight, RefreshCw,
  User, Bot, Image, Trash2, Filter, MoreVertical
} from 'lucide-react';
import api from '@/services/api';
import { API_BASE, getToken } from '@/lib/api';

// ═══════════════════════════════════════════════════════════════════════════════
// LOCAL CHAT TAB
// ═══════════════════════════════════════════════════════════════════════════════
const LocalChatTab = ({ userId, theme }) => {
  const isDark = theme === 'dark';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChatHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/api/smart-assistant/chat/${userId}`);
      if (response.data.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Save user message
      await api.post('/api/smart-assistant/chat', {
        userId,
        role: 'user',
        content: input
      });

      // Generate AI response (simulated - integrate with actual AI service)
      const aiResponse = generateERPHelp(input);
      
      const assistantMessage = { role: 'assistant', content: aiResponse, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message
      await api.post('/api/smart-assistant/chat', {
        userId,
        role: 'assistant',
        content: aiResponse
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  // Simple ERP help response generator
  const generateERPHelp = (query) => {
    const q = query.toLowerCase();
    if (q.includes('voucher') || q.includes('entry')) {
      return "To create a voucher:\n1. Go to Financial Accounting → Vouchers\n2. Select voucher type (Receipt, Payment, Journal)\n3. Enter date, narration, and ledger entries\n4. Click Save to post the voucher";
    }
    if (q.includes('trial balance') || q.includes('balance')) {
      return "To view Trial Balance:\n1. Navigate to Financial Accounting → Trial Balance\n2. Select the date range\n3. Click 'Generate' to view balances\n4. Use Export button to download in Excel/PDF";
    }
    if (q.includes('report') || q.includes('export')) {
      return "Reports can be exported from any report screen:\n1. Open the desired report\n2. Apply any filters\n3. Click the Export button (top-right)\n4. Choose format: Excel, PDF, CSV, or Print";
    }
    if (q.includes('bank') || q.includes('reconciliation')) {
      return "For Bank Reconciliation:\n1. Go to Financial Accounting → Bank Reconciliation\n2. Select bank account and date range\n3. Match transactions with bank statement\n4. Mark cleared items and save";
    }
    return "I can help you with:\n• Creating vouchers and entries\n• Viewing reports (Trial Balance, Ledgers, etc.)\n• Bank reconciliation\n• Inventory management\n• User settings\n\nPlease ask a specific question about any ERP feature!";
  };

  const clearChat = async () => {
    try {
      await api.delete(`/api/smart-assistant/chat/${userId}`);
      setMessages([]);
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`shrink-0 px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <Bot size={16} className="text-blue-500" />
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>ERP Assistant</span>
        </div>
        <button 
          onClick={clearChat}
          className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-500' : 'hover:bg-gray-100 text-gray-400'}`}
          title="Clear chat"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {historyLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={20} className="animate-spin text-blue-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <Bot className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              Ask me anything about the ERP system!
            </p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                  <Bot size={14} />
                </div>
              )}
              <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : isDark ? 'bg-[#222] text-slate-300 rounded-bl-sm' : 'bg-gray-100 text-gray-700 rounded-bl-sm'
              }`}>
                {msg.content}
              </div>
              {msg.role === 'user' && (
                <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center bg-blue-600 text-white`}>
                  <User size={14} />
                </div>
              )}
            </div>
          ))
        )}
        {loading && (
          <div className="flex gap-3">
            <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
              <Bot size={14} />
            </div>
            <div className={`px-4 py-3 rounded-xl ${isDark ? 'bg-[#222]' : 'bg-gray-100'}`}>
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={`shrink-0 p-3 border-t ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-gray-50 border-gray-200'}`}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask about ERP features..."
            className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder:text-slate-600' : 'text-gray-900 placeholder:text-gray-400'}`}
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className={`p-2 rounded-lg transition-colors ${input.trim() ? 'bg-blue-600 text-white' : isDark ? 'bg-[#333] text-slate-600' : 'bg-gray-200 text-gray-400'}`}
            data-testid="chat-send-btn"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUPPORT TICKETS TAB - Integrated with TicketDesk & WhatsApp
// ═══════════════════════════════════════════════════════════════════════════════
const SupportTicketsTab = ({ userId, userName, userEmail, userPhone, clientId, theme }) => {
  const isDark = theme === 'dark';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all');
  const [ticketDeskConnected, setTicketDeskConnected] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'General',
    priority: 'Medium',
    notifyWhatsApp: true
  });
  const [newComment, setNewComment] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticketStats, setTicketStats] = useState({ total: 0, open: 0, resolved: 0 });
  const fileInputRef = React.useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf', '.docx', '.xlsx'];

  useEffect(() => {
    checkTicketDeskConnection();
    loadTickets();
    loadTicketStats();
  }, [userId, filter]);

  // Check TicketDesk connection status
  const checkTicketDeskConnection = async () => {
    try {
      const response = await api.get('/api/ticketdesk/health');
      setTicketDeskConnected(response.data.connected);
      setWhatsappEnabled(response.data.whatsappEnabled);
    } catch (error) {
      setTicketDeskConnected(false);
      setWhatsappEnabled(false);
    }
  };

  // Load ticket statistics
  const loadTicketStats = async () => {
    try {
      // Try TicketDesk first if connected
      if (ticketDeskConnected) {
        try {
          const response = await api.get(`/api/ticketdesk/stats?userId=${userId}&clientId=${clientId || ''}`);
          if (response.data.success) {
            setTicketStats(response.data.data);
            return;
          }
        } catch (e) {
          console.warn('TicketDesk stats not available:', e);
        }
      }
      
      // Fallback - count from local tickets
      try {
        const response = await api.get(`/api/smart-assistant/tickets/${userId}`);
        if (response.data.success) {
          const tickets = response.data.data || [];
          setTicketStats({
            total: tickets.length,
            open: tickets.filter(t => t.status === 'Open').length,
            inProgress: tickets.filter(t => t.status === 'In Progress').length,
            resolved: tickets.filter(t => t.status === 'Resolved').length,
            closed: tickets.filter(t => t.status === 'Closed').length
          });
        }
      } catch (e) {
        console.error('Error loading ticket stats:', e);
      }
    } catch (error) {
      console.error('Error loading ticket stats:', error);
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      // Try TicketDesk first if connected
      if (ticketDeskConnected) {
        try {
          const response = await api.get(`/api/ticketdesk/tickets?userId=${userId}&status=${filter}`);
          if (response.data.success) {
            setTickets(response.data.data);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn('TicketDesk not available, falling back to local:', e);
        }
      }
      
      // Fallback to local tickets
      const response = await api.get(`/api/smart-assistant/tickets/${userId}?status=${filter}`);
      if (response.data.success) {
        setTickets(response.data.data);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim()) return;
    setSubmitting(true);
    try {
      let response;
      
      // Try TicketDesk first if connected
      if (ticketDeskConnected) {
        try {
          response = await api.post('/api/ticketdesk/tickets', {
            userId,
            userName,
            userEmail,
            userPhone,
            clientId,
            subject: newTicket.subject,
            description: newTicket.description,
            category: newTicket.category,
            priority: newTicket.priority,
            screenContext: window.location.pathname,
            erpModule: 'RetailCLIQ ERP',
            notifyWhatsApp: newTicket.notifyWhatsApp && whatsappEnabled
          });
        } catch (e) {
          console.warn('TicketDesk create failed, falling back to local:', e);
        }
      }
      
      // Fallback to local ticket creation
      if (!response?.data?.success) {
        response = await api.post('/api/smart-assistant/tickets', {
          userId,
          subject: newTicket.subject,
          description: newTicket.description,
          category: newTicket.category,
          priority: newTicket.priority
        });
      }
      
      if (response?.data?.success) {
        const ticketId = response.data.data.id;
        
        // Upload pending files if any
        if (pendingFiles.length > 0) {
          await uploadFilesToTicket(ticketId);
        }
        
        setShowNewTicket(false);
        setNewTicket({ subject: '', description: '', category: 'General', priority: 'Medium', notifyWhatsApp: true });
        setPendingFiles([]);
        loadTickets();
        loadTicketStats();
        
        // Show success message
        const ticketNumber = response.data.data.ticketNumber || response.data.data.TicketNumber || `#${ticketId}`;
        alert(`Ticket ${ticketNumber} created successfully!${newTicket.notifyWhatsApp && whatsappEnabled ? '\nSupport team has been notified via WhatsApp.' : ''}`);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // File handling functions
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        alert(`File type not allowed: ${file.name}. Allowed: PNG, JPG, PDF, DOCX, XLSX`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large: ${file.name}. Maximum size: 10MB`);
        return false;
      }
      return true;
    });
    setPendingFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFilesToTicket = async (ticketId) => {
    if (pendingFiles.length === 0) return;
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      pendingFiles.forEach(file => formData.append('files', file));
      formData.append('userId', userId);
      
      await api.post(`/api/smart-assistant/tickets/${ticketId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploadingFiles(false);
    }
  };

  const downloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await api.get(`/api/smart-assistant/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!confirm('Delete this attachment?')) return;
    try {
      await api.delete(`/api/smart-assistant/attachments/${attachmentId}`);
      if (selectedTicket) {
        loadTicketDetail(selectedTicket.id);
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('sheet') || fileType?.includes('excel')) return '📊';
    return '📎';
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const response = await api.get(`/api/smart-assistant/tickets/detail/${ticketId}`);
      if (response.data.success) {
        setSelectedTicket(response.data.data);
      }
    } catch (error) {
      console.error('Error loading ticket detail:', error);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    try {
      await api.post(`/api/smart-assistant/tickets/${selectedTicket.id}/comments`, {
        userId,
        content: newComment
      });
      setNewComment('');
      loadTicketDetail(selectedTicket.id);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await api.put(`/api/smart-assistant/tickets/${ticketId}/status`, { status });
      loadTickets();
      if (selectedTicket?.id === ticketId) {
        loadTicketDetail(ticketId);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-500 bg-red-500/10';
      case 'Medium': return 'text-amber-500 bg-amber-500/10';
      case 'Low': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'text-blue-500 bg-blue-500/10';
      case 'In Progress': return 'text-amber-500 bg-amber-500/10';
      case 'Closed': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className={`shrink-0 px-4 py-3 border-b flex items-center gap-3 ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <button onClick={() => setSelectedTicket(null)} className={`p-1 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <ChevronLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedTicket.ticketNumber}</p>
            <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{selectedTicket.subject}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${getStatusColor(selectedTicket.status)}`}>
            {selectedTicket.status}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className={`p-3 rounded-lg mb-4 ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-50'}`}>
            <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{selectedTicket.description || 'No description'}</p>
            <div className="flex items-center gap-3 mt-3 text-xs">
              <span className={`px-2 py-0.5 rounded ${getPriorityColor(selectedTicket.priority)}`}>{selectedTicket.priority}</span>
              <span className={isDark ? 'text-slate-500' : 'text-gray-500'}>{selectedTicket.category}</span>
            </div>
          </div>

          {/* Comments */}
          <div className="space-y-3">
            {selectedTicket.comments?.map((comment, idx) => (
              <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-[#222]' : 'bg-gray-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-blue-600' : 'bg-blue-600'} text-white`}>
                    {comment.userId.charAt(0).toUpperCase()}
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{comment.userId}</span>
                  <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                    {new Date(comment.createdAt).toLocaleString()}
                  </span>
                </div>
                <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{comment.content}</p>
              </div>
            ))}
          </div>

          {/* Attachments */}
          {selectedTicket.attachments?.length > 0 && (
            <div className="mt-4">
              <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                📎 Attachments ({selectedTicket.attachments.length})
              </p>
              <div className="space-y-1">
                {selectedTicket.attachments.map((att) => (
                  <div key={att.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-50'}`}>
                    <span>{getFileIcon(att.fileType)}</span>
                    <span className={`flex-1 truncate text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{att.fileName}</span>
                    <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{formatFileSize(att.fileSize)}</span>
                    <button
                      onClick={() => downloadAttachment(att.id, att.fileName)}
                      className={`p-1 rounded ${isDark ? 'hover:bg-blue-500/20 text-blue-400' : 'hover:bg-blue-50 text-blue-600'}`}
                      title="Download"
                    >
                      <ArrowDownRight size={12} />
                    </button>
                    <button
                      onClick={() => deleteAttachment(att.id)}
                      className={`p-1 rounded ${isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`shrink-0 p-3 border-t ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <div className="flex gap-2 mb-2">
            {['Open', 'In Progress', 'Closed'].map(status => (
              <button
                key={status}
                onClick={() => updateStatus(selectedTicket.id, status)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  selectedTicket.status === status
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'bg-[#222] text-slate-400 hover:bg-[#333]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-gray-50 border-gray-200'}`}>
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addComment()}
              placeholder="Add a comment..."
              className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder:text-slate-600' : 'text-gray-900 placeholder:text-gray-400'}`}
            />
            <button onClick={addComment} disabled={!newComment.trim()} className={`p-1.5 rounded-lg ${newComment.trim() ? 'bg-blue-600 text-white' : isDark ? 'bg-[#333] text-slate-600' : 'bg-gray-200 text-gray-400'}`}>
              <Send size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // New Ticket Form
  if (showNewTicket) {
    return (
      <div className="flex flex-col h-full">
        <div className={`shrink-0 px-4 py-3 border-b flex items-center gap-3 ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <button onClick={() => setShowNewTicket(false)} className={`p-1 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <ChevronLeft size={18} />
          </button>
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>New Support Ticket</span>
          {ticketDeskConnected && (
            <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold bg-green-500/10 text-green-500">
              TicketDesk Connected
            </span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Subject *</label>
            <input
              type="text"
              value={newTicket.subject}
              onChange={(e) => setNewTicket(p => ({ ...p, subject: e.target.value }))}
              placeholder="Brief description of issue"
              className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'}`}
            />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Description</label>
            <textarea
              value={newTicket.description}
              onChange={(e) => setNewTicket(p => ({ ...p, description: e.target.value }))}
              placeholder="Detailed description..."
              rows={4}
              className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Category</label>
              <select
                value={newTicket.category}
                onChange={(e) => setNewTicket(p => ({ ...p, category: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'}`}
              >
                <option value="General">General</option>
                <option value="Bug">Bug</option>
                <option value="Feature Request">Feature Request</option>
                <option value="Question">Question</option>
                <option value="Finance">Finance</option>
                <option value="Inventory">Inventory</option>
                <option value="Reports">Reports</option>
                <option value="POS">POS System</option>
                <option value="Payroll">Payroll</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Priority</label>
              <select
                value={newTicket.priority}
                onChange={(e) => setNewTicket(p => ({ ...p, priority: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'}`}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>

          {/* WhatsApp Notification Toggle */}
          {whatsappEnabled && (
            <div className={`p-3 rounded-lg flex items-center gap-3 ${isDark ? 'bg-green-900/20 border border-green-800/30' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex-1">
                <p className={`text-xs font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                  📱 WhatsApp Notification
                </p>
                <p className={`text-[10px] ${isDark ? 'text-green-500/70' : 'text-green-600'}`}>
                  Notify support team instantly via WhatsApp
                </p>
              </div>
              <button
                onClick={() => setNewTicket(p => ({ ...p, notifyWhatsApp: !p.notifyWhatsApp }))}
                className={`relative w-10 h-5 rounded-full transition-colors ${newTicket.notifyWhatsApp ? 'bg-green-500' : isDark ? 'bg-[#333]' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${newTicket.notifyWhatsApp ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          )}

          {/* File Attachments */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Attachments</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept=".png,.jpg,.jpeg,.pdf,.docx,.xlsx"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`w-full px-3 py-2.5 rounded-lg border border-dashed text-sm flex items-center justify-center gap-2 transition-colors ${
                isDark ? 'border-[#444] text-slate-400 hover:border-blue-500 hover:text-blue-400' : 'border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-600'
              }`}
            >
              <Paperclip size={14} />
              Add Files (PNG, JPG, PDF, DOCX, XLSX)
            </button>
            <p className={`text-[10px] mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
              Max 10MB per file
            </p>

            {/* Pending Files List */}
            {pendingFiles.length > 0 && (
              <div className="mt-2 space-y-1">
                {pendingFiles.map((file, idx) => (
                  <div key={idx} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-50'}`}>
                    <span>{getFileIcon(file.type)}</span>
                    <span className={`flex-1 truncate ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{file.name}</span>
                    <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>{formatFileSize(file.size)}</span>
                    <button
                      onClick={() => removePendingFile(idx)}
                      className={`p-0.5 rounded ${isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={`shrink-0 p-4 border-t ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <button
            onClick={createTicket}
            disabled={!newTicket.subject.trim() || uploadingFiles || submitting}
            className={`w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              newTicket.subject.trim() && !uploadingFiles && !submitting
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : isDark ? 'bg-[#222] text-slate-600' : 'bg-gray-100 text-gray-400'
            }`}
          >
            {submitting ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Creating Ticket...
              </>
            ) : uploadingFiles ? (
              'Uploading...'
            ) : (
              <>
                <Ticket size={14} />
                {pendingFiles.length > 0 ? `Create Ticket (${pendingFiles.length} files)` : 'Create Ticket'}
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Tickets List
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`shrink-0 px-4 py-3 border-b ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Ticket size={16} className="text-purple-500" />
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Tickets</span>
            {ticketDeskConnected && (
              <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-green-500/10 text-green-500">LIVE</span>
            )}
          </div>
          <button
            onClick={() => setShowNewTicket(true)}
            className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
            data-testid="new-ticket-btn"
          >
            <Plus size={12} />
            New
          </button>
        </div>

        {/* Ticket Stats */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-50'}`}>
            <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticketStats.total}</p>
            <p className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total</p>
          </div>
          <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
            <p className="text-lg font-bold text-blue-500">{ticketStats.open}</p>
            <p className={`text-[9px] ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Open</p>
          </div>
          <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
            <p className="text-lg font-bold text-amber-500">{ticketStats.inProgress || 0}</p>
            <p className={`text-[9px] ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Progress</p>
          </div>
          <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
            <p className="text-lg font-bold text-green-500">{ticketStats.resolved}</p>
            <p className={`text-[9px] ${isDark ? 'text-green-400' : 'text-green-600'}`}>Resolved</p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1">
          {['all', 'Open', 'In Progress', 'Resolved', 'Closed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : isDark ? 'text-slate-500 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      {/* Tickets List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={20} className="animate-spin text-blue-500" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-8">
            <Ticket className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>No tickets found</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Create a new ticket to get support</p>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {tickets.map(ticket => (
              <button
                key={ticket.id}
                onClick={() => loadTicketDetail(ticket.id)}
                className={`w-full text-left p-3 rounded-xl border transition-colors ${
                  isDark ? 'bg-[#1a1a1a] border-[#333] hover:border-[#444]' : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</p>
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={isDark ? 'text-slate-600' : 'text-gray-400'}>{ticket.ticketNumber}</span>
                  <span className={`px-1.5 py-0.5 rounded ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                  {ticket.commentCount > 0 && (
                    <span className={isDark ? 'text-slate-500' : 'text-gray-500'}>💬 {ticket.commentCount}</span>
                  )}
                </div>
                <p className={`text-[10px] mt-1.5 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                  {new Date(ticket.createdAt).toLocaleDateString()} • {ticket.category}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MARKET RATES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const MarketRatesTab = ({ theme }) => {
  const isDark = theme === 'dark';
  const [view, setView] = useState('currency'); // 'currency' | 'mcx'
  const [currencyRates, setCurrencyRates] = useState(null);
  const [mcxRates, setMcxRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    loadRates();
    const interval = setInterval(loadRates, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadRates = async () => {
    setLoading(true);
    try {
      const [currencyRes, mcxRes] = await Promise.all([
        api.get('/api/smart-assistant/market/currency'),
        api.get('/api/smart-assistant/market/mcx')
      ]);
      if (currencyRes.data.success) setCurrencyRates(currencyRes.data.data);
      if (mcxRes.data.success) setMcxRates(mcxRes.data.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading market rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const RateCard = ({ symbol, data, unit = '' }) => (
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-bold ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{symbol}</span>
        {unit && <span className={`text-[9px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{unit}</span>}
      </div>
      <div className="flex items-end justify-between">
        <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {formatCurrency(data.rate || data.price)}
        </span>
        <div className={`flex items-center gap-0.5 text-xs font-medium ${data.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {data.change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          <span>{formatCurrency(Math.abs(data.change))}</span>
          <span className="opacity-70">({formatCurrency(Math.abs(data.changePercent))}%)</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`shrink-0 px-4 py-3 border-b ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-green-500" />
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Market Rates</span>
          </div>
          <button onClick={loadRates} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-500' : 'hover:bg-gray-100 text-gray-400'}`}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        {/* View Toggle */}
        <div className={`flex p-0.5 rounded-lg ${isDark ? 'bg-[#222]' : 'bg-gray-100'}`}>
          <button
            onClick={() => setView('currency')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === 'currency'
                ? 'bg-blue-600 text-white'
                : isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            💱 Currency
          </button>
          <button
            onClick={() => setView('mcx')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === 'mcx'
                ? 'bg-blue-600 text-white'
                : isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📊 MCX
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading && !currencyRates && !mcxRates ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={20} className="animate-spin text-blue-500" />
          </div>
        ) : view === 'currency' && currencyRates ? (
          <div className="space-y-2">
            {Object.entries(currencyRates.rates).map(([symbol, data]) => (
              <RateCard key={symbol} symbol={`${symbol}/INR`} data={data} />
            ))}
          </div>
        ) : view === 'mcx' && mcxRates ? (
          <div className="space-y-2">
            {Object.entries(mcxRates.commodities).map(([symbol, data]) => (
              <RateCard key={symbol} symbol={symbol} data={data} unit={data.unit} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Unable to load rates</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastUpdate && (
        <div className={`shrink-0 px-4 py-2 border-t text-center ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <p className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CLIQ AI DESK TAB — embeds the full Cliq Smart ERP Assistant widget (caller
// search, AI suggest/enhance, knowledge base, customer portal) via SSO iframe
// ═══════════════════════════════════════════════════════════════════════════════
const CliqWidgetTab = ({ userId, userName, companyName, theme }) => {
  const isDark = theme === 'dark';
  const src = useMemo(() => {
    const token = getToken();
    const params = new URLSearchParams({
      mode: 'agent',
      agentToken: token || '',
      agentName: userName || userId || 'Agent',
      agentCompany: companyName || '',
      color: isDark ? '#3b82f6' : '#42c86e',
    });
    return `${API_BASE}/api/widget-assets/assistant.html?${params.toString()}`;
  }, [userId, userName, companyName, isDark]);

  return (
    <iframe
      key={src}
      src={src}
      title="Cliq AI Desk"
      data-testid="cliq-ai-desk-iframe"
      className="w-full h-full border-0"
      allow="clipboard-write"
    />
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN SMART ASSISTANT PANEL
// ═══════════════════════════════════════════════════════════════════════════════
const SmartAssistantPanel = ({ 
  isOpen, 
  onToggle, 
  userId = 'admin',
  userName = '',
  userEmail = '',
  userPhone = '',
  clientId = '',
  companyName = '',
  theme = 'light' 
}) => {
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('chat');
  const [stats, setStats] = useState({ tickets: { open: 0 }, notifications: { unread: 0 }, alerts: { unacknowledged: 0 } });

  useEffect(() => {
    if (isOpen) {
      loadStats();
      initTables();
    }
  }, [isOpen, userId]);

  const initTables = async () => {
    try {
      await api.post('/api/smart-assistant/init-tables');
    } catch (error) {
      // Tables might already exist
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get(`/api/smart-assistant/stats/${userId}`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const tabs = [
    { id: 'chat', label: 'AI Help', icon: MessageSquare, badge: 0 },
    { id: 'tickets', label: 'Tickets', icon: Ticket, badge: stats.tickets?.open || 0 },
    { id: 'ai_desk', label: 'AI Desk', icon: Bot, badge: 0 },
    { id: 'market', label: 'Rates', icon: TrendingUp, badge: 0 },
  ];

  return (
    <>
      {/* Toggle Button - Always Visible */}
      <button
        onClick={onToggle}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-[99] px-1 py-4 rounded-l-xl shadow-lg transition-all ${
          isOpen ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
        } ${isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'}`}
        data-testid="assistant-toggle-btn"
        title="Smart Assistant"
      >
        <ChevronLeft size={16} />
      </button>

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 z-[100] shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isDark ? 'bg-[#111] border-l border-[#333]' : 'bg-white border-l border-gray-200'}`}
        data-testid="smart-assistant-panel"
      >
        {/* Panel Header */}
        <div className={`h-11 flex items-center justify-between px-3 border-b shrink-0 ${isDark ? 'border-[#333] bg-[#0e0e10]' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-amber-500" />
            <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Smart Assistant</span>
          </div>
          <button
            onClick={onToggle}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
            data-testid="assistant-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors relative ${
                activeTab === tab.id
                  ? isDark ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600'
                  : isDark ? 'text-slate-500 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              }`}
              data-testid={`assistant-tab-${tab.id}`}
            >
              <tab.icon size={14} />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-0.5 right-2 w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 h-[calc(100%-88px)] overflow-hidden">
          {activeTab === 'chat' && <LocalChatTab userId={userId} theme={theme} />}
          {activeTab === 'tickets' && <SupportTicketsTab userId={userId} userName={userName} userEmail={userEmail} userPhone={userPhone} clientId={clientId} theme={theme} />}
          {activeTab === 'ai_desk' && <CliqWidgetTab userId={userId} userName={userName} companyName={companyName} theme={theme} />}
          {activeTab === 'market' && <MarketRatesTab theme={theme} />}
        </div>
      </div>
    </>
  );
};

export default SmartAssistantPanel;
